import {lazy, FC, Suspense} from 'react'
import {Route, Routes, Navigate, useLocation} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {MenuTestPage} from '../pages/MenuTestPage'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {WithChildren} from '../../_metronic/helpers'
import {MenuModel, useAuth} from '../modules/auth'
import UsersPage from '../modules/apps/user-management/UsersPage'
import JudolPage from '../modules/judol/JudolPage'
import JudiPage from '../modules/judi/JudiPage'

const PrivateRoutes = () => {
  const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))
  const UserRegistrationPage = lazy(
    () => import('../modules/user-registration/UserRegistrationPage')
  )
  const UserPage = lazy(() => import('../modules/user/UserPage'))
  const GakkumReportPage = lazy(() => import('../modules/reports/GakkumReportPage'))
  const DashboardWrapper = lazy(() => import('../pages/dashboard/DashboardWrapper'))
  const OtherReportPage = lazy(() => import('../modules/reports/others/OtherReportPage'))
  const TipidkorPage = lazy(() => import('../modules/tipidkor/TipidkorPage'))
  const NarkobaPage = lazy(() => import('../modules/narkoba/NarkobaPage'))
  const LundupPage = lazy(() => import('../modules/lundup/LundupPage'))
  const TppoPage = lazy(() => import('../modules/tppo/TppoPage'))
  const CitraPage = lazy(() => import('../modules/citra/CitraPage'))
  const PencegahanPage = lazy(() => import('../modules/pencegahan/PencegahanPage'))
  const ReleaseMaterialPage = lazy(() => import('../modules/release-material/ReleaseMaterialPage'))
  const SocializationPage = lazy(() => import('../modules/socialization/SocializationPage'))
  const BlockedWebPage = lazy(() => import('../modules/blocked-web/BlockedWebPage'))
  const AdhocPage = lazy(() => import('../modules/adhoc/AdhocPage'))

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        <Route path='auth/*' element={<Navigate to='/dashboard' />} />
        {/* Pages */}
        <Route
          path='dashboard'
          element={
            <SuspensedView>
              <DashboardWrapper />
            </SuspensedView>
          }
        />
        <Route
          path='profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route path='menu-test' element={<MenuTestPage />} />
        {/* Lazy Modules */}
        <Route
          path='user-session/*'
          element={
            <SuspensedView>
              <ProtectedRoute>
                <UsersPage />
              </ProtectedRoute>
            </SuspensedView>
          }
        />
        <Route
          path='reports/others/*'
          element={
            <SuspensedView>
              <ProtectedRoute>
                <OtherReportPage />
              </ProtectedRoute>
            </SuspensedView>
          }
        />
        <Route
          path='summary/*'
          element={
            <SuspensedView>
              <ProtectedRoute>
                <GakkumReportPage />
              </ProtectedRoute>
            </SuspensedView>
          }
        />
        <Route
          path='user-registration/*'
          element={
            <SuspensedView>
              <ProtectedRoute>
                <UserRegistrationPage />
              </ProtectedRoute>
            </SuspensedView>
          }
        ></Route>
        <Route
          path='users/*'
          element={
            <SuspensedView>
              <ProtectedRoute>
                <UserPage />
              </ProtectedRoute>
            </SuspensedView>
          }
        ></Route>
        <Route
          path='tipidkor/*'
          element={
            <SuspensedView>
              <ProtectedRoute>
                <TipidkorPage />
              </ProtectedRoute>
            </SuspensedView>
          }
        ></Route>
        <Route
          path='judol/*'
          element={
            <SuspensedView>
              <ProtectedRoute>
                <JudolPage />
              </ProtectedRoute>
            </SuspensedView>
          }
        />
        <Route
          path='narkoba/*'
          element={
            <SuspensedView>
              <ProtectedRoute>
                <NarkobaPage />
              </ProtectedRoute>
            </SuspensedView>
          }
        />
        <Route
          path='judi/*'
          element={
            <SuspensedView>
              <ProtectedRoute>
                <JudiPage />
              </ProtectedRoute>
            </SuspensedView>
          }
        />
        <Route
          path='lundup/*'
          element={
            <SuspensedView>
              <ProtectedRoute>
                <LundupPage />
              </ProtectedRoute>
            </SuspensedView>
          }
        />
        <Route
          path='tppo/*'
          element={
            <SuspensedView>
              <ProtectedRoute>
                <TppoPage />
              </ProtectedRoute>
            </SuspensedView>
          }
        />
        <Route
          path='manage-citra/*'
          element={
            <SuspensedView>
              <ProtectedRoute>
                <CitraPage />
              </ProtectedRoute>
            </SuspensedView>
          }
        />
        <Route
          path='pencegahan/*'
          element={
            <SuspensedView>
              <ProtectedRoute>
                <PencegahanPage />
              </ProtectedRoute>
            </SuspensedView>
          }
        />
        <Route
          path='bahan-rilis/*'
          element={
            <SuspensedView>
              <ProtectedRoute>
                <ReleaseMaterialPage />
              </ProtectedRoute>
            </SuspensedView>
          }
        />
        <Route
          path='bahan-sosialisasi/*'
          element={
            <SuspensedView>
              <ProtectedRoute>
                <SocializationPage />
              </ProtectedRoute>
            </SuspensedView>
          }
        />
        <Route
          path='blokir-list/*'
          element={
            <SuspensedView>
              <ProtectedRoute>
                <BlockedWebPage />
              </ProtectedRoute>
            </SuspensedView>
          }
        />
        <Route
          path='adhoc/*'
          element={
            <SuspensedView>
              <ProtectedRoute>
                <AdhocPage />
              </ProtectedRoute>
            </SuspensedView>
          }
        />
        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

const ProtectedRoute = ({redirectPath = '/error/404', children}) => {
  const {pathname} = useLocation()
  const {currentUser, currentMenu} = useAuth()
  if (!currentUser || !currentMenu) {
    return children
  }

  let isAllowed = isMenuAccessible(currentMenu, pathname)

  if (!isAllowed) {
    return <Navigate to={redirectPath} replace />
  }

  return children
}

function canAccessMenu(menu: MenuModel, path: string) {
  const pattern = new RegExp(menu.route)
  if (pattern.test(path)) {
    return true
  }

  if (menu.children) {
    return menu.children.some((child) => canAccessMenu(child, path))
  }

  return false
}

function isMenuAccessible(menuData: MenuModel[], path: string) {
  return menuData.some((route) => canAccessMenu(route, path))
}

export {PrivateRoutes}
